import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  faPlus, 
  faEye, 
  faTrash,
  faTools,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../services/api';
import { useCustomLoading } from '../hooks/useCustomLoading';

const RepairPlanning = () => {
  const navigate = useNavigate();
  const { withLoading } = useCustomLoading();
  
  // State
  const [bridges, setBridges] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [filteredComponents, setFilteredComponents] = useState([]);
  const [componentSearchTerm, setComponentSearchTerm] = useState('');
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [repairProjects, setRepairProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination and filters
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [filters, setFilters] = useState({
    bridgeId: '',
    searchTerm: '',
    plannedYear: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    bridgeId: '',
    projectName: '',
    plannedYear: new Date().getFullYear()
  });

  useEffect(() => {
    fetchBridges();
  }, []);

  useEffect(() => {
    if (formData.bridgeId) {
      fetchAvailableComponents(formData.bridgeId);
    }
  }, [formData.bridgeId]);

  const fetchBridges = async () => {
    try {
      const response = await api.get('/bridges?limit=100');
      if (response.data.success) {
        setBridges(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching bridges:', error);
      setError('Failed to fetch bridges');
    }
  };

  const fetchAvailableComponents = async (bridgeId) => {
    if (!bridgeId) return;
    
    try {
      const response = await api.get(`/components?bridgeId=${bridgeId}&limit=5000`);
      if (response.data.success) {
        setAvailableComponents(response.data.data);
        setFilteredComponents(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching components:', error);
    }
  };

  const filterComponents = (searchTerm) => {
    setComponentSearchTerm(searchTerm);
    
    if (!searchTerm.trim()) {
      setFilteredComponents(availableComponents);
      return;
    }

    const filtered = availableComponents.filter(component => {
      const searchLower = searchTerm.toLowerCase();
      return (
        component.componentName?.toLowerCase().includes(searchLower) ||
        component.subComponentName?.toLowerCase().includes(searchLower) ||
        component.asset?.toLowerCase().includes(searchLower) ||
        component.direction?.toLowerCase().includes(searchLower) ||
        component.frame?.toLowerCase().includes(searchLower) ||
        component.element?.toLowerCase().includes(searchLower) ||
        component.panel?.toLowerCase().includes(searchLower) ||
        component.componentType?.toLowerCase().includes(searchLower) ||
        component.internalExternal?.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredComponents(filtered);
  };

  const fetchRepairProjects = useCallback(async (page = 1, limit = 50) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (filters.bridgeId) params.append('bridge_id', filters.bridgeId);
      if (filters.searchTerm) params.append('search', filters.searchTerm);
      if (filters.plannedYear) params.append('planned_year', filters.plannedYear);
      
      const response = await api.get(`/repair-planning?${params.toString()}`);
      if (response.data.success) {
        setRepairProjects(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching repair projects:', error);
      setError('Failed to fetch repair projects');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch projects when filters or page size change
  useEffect(() => {
    fetchRepairProjects(1, pagination.itemsPerPage);
  }, [filters, pagination.itemsPerPage, fetchRepairProjects]);

  const handleComponentSelection = (componentId) => {
    setSelectedComponents(prev => {
      if (prev.includes(componentId)) {
        return prev.filter(id => id !== componentId);
      } else {
        return [...prev, componentId];
      }
    });
  };

  const createRepairProject = async () => {
    if (selectedComponents.length === 0) {
      setError('Please select at least one component');
      return;
    }

    if (!formData.projectName.trim()) {
      setError('Please enter a project name');
      return;
    }

    try {
      await withLoading(async () => {
        const response = await api.post('/repair-planning', {
          ...formData,
          componentIds: selectedComponents,
          status: 'planned'
        });

        if (response.data.success) {
          setRepairProjects(prev => [response.data.data, ...prev]);
          setFormData({
            bridgeId: '',
            projectName: '',
            plannedYear: new Date().getFullYear()
          });
          setSelectedComponents([]);
          setComponentSearchTerm('');
          setError(null);
        }
      }, 'Creating repair project...');
    } catch (error) {
      console.error('Error creating repair project:', error);
      setError('Failed to create repair project');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this repair project?')) {
      return;
    }

    try {
      await withLoading(async () => {
        await api.delete(`/repair-planning/${projectId}`);
        fetchRepairProjects(pagination.currentPage, pagination.itemsPerPage);
      }, 'Deleting repair project...');
    } catch (error) {
      console.error('Error deleting project:', error);
      setError('Failed to delete repair project');
    }
  };

  // Filter and pagination handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePageChange = (newPage) => {
    fetchRepairProjects(newPage, pagination.itemsPerPage);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setPagination(prev => ({ ...prev, itemsPerPage: newLimit }));
    fetchRepairProjects(1, newLimit);
  };

  const clearFilters = () => {
    setFilters({
      bridgeId: '',
      searchTerm: '',
      plannedYear: ''
    });
  };


  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Repair Planning</h1>
        <p className="text-gray-600">Create and manage repair projects for bridge components</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Create Repair Project Form */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Create New Repair Project</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bridge</label>
            <select
              value={formData.bridgeId}
              onChange={(e) => setFormData({ ...formData, bridgeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a bridge</option>
              {bridges.map(bridge => (
                <option key={bridge.id} value={bridge.id}>
                  {bridge.bridgeName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter project name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Planned Year</label>
            <input
              type="number"
              value={formData.plannedYear}
              onChange={(e) => setFormData({ ...formData, plannedYear: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min={new Date().getFullYear()}
              max={new Date().getFullYear() + 10}
            />
          </div>
        </div>

        {/* Component Selection */}
        {formData.bridgeId && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-800 mb-3">Select Components</h3>
            
            {/* Search Input */}
            <div className="mb-4">
              <input
                type="text"
                value={componentSearchTerm}
                onChange={(e) => filterComponents(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search components by name, location, or type..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Showing {filteredComponents.length} of {availableComponents.length} components
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
              {filteredComponents.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  {componentSearchTerm ? 'No components found matching your search' : 'No components available for this bridge'}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredComponents.map(component => (
                    <label key={component.id} className="flex items-start space-x-3 cursor-pointer hover:bg-gray-100 p-3 rounded border">
                      <input
                        type="checkbox"
                        checked={selectedComponents.includes(component.id)}
                        onChange={() => handleComponentSelection(component.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-700">
                          <div className="font-medium">{component.componentName}</div>
                          {(component.subComponentName || component.asset || component.direction || component.frame || component.element || component.panel) && (
                            <div className="text-xs text-gray-500 mt-1">
                              {[
                                component.asset && `Asset: ${component.asset}`,
                                component.direction && `Dir: ${component.direction}`,
                                component.frame && `Frame: ${component.frame}`,
                                component.element && `Element: ${component.element}`,
                                component.panel && `Panel: ${component.panel}`,
                                component.subComponentName && `Sub: ${component.subComponentName}`
                              ].filter(Boolean).join(' • ')}
                            </div>
                          )}
                          {component.inventory?.surfaceArea && (
                            <div className="text-xs text-blue-600 mt-1 font-medium">
                              Surface Area: {component.inventory.surfaceArea}m²
                            </div>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Selected: {selectedComponents.length} component{selectedComponents.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={createRepairProject}
            disabled={selectedComponents.length === 0 || !formData.projectName.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            Create Repair Project
          </button>
        </div>
      </div>

      {/* Repair Projects List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Repair Projects</h2>
            <div className="text-sm text-gray-500">
              {pagination.totalItems} total projects
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bridge</label>
              <select
                value={filters.bridgeId}
                onChange={(e) => handleFilterChange('bridgeId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Bridges</option>
                {bridges.map(bridge => (
                  <option key={bridge.id} value={bridge.id}>
                    {bridge.bridgeName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                placeholder="Project name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Planned Year</label>
              <input
                type="number"
                value={filters.plannedYear}
                onChange={(e) => handleFilterChange('plannedYear', e.target.value)}
                placeholder="Year..."
                min="2020"
                max="2030"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-400 mb-4" />
            <p className="text-gray-500">Loading repair projects...</p>
          </div>
        ) : repairProjects.length === 0 ? (
          <div className="p-8 text-center">
            <FontAwesomeIcon icon={faTools} className="text-4xl text-gray-400 mb-4" />
            <p className="text-gray-500">No repair projects found</p>
            <p className="text-sm text-gray-400 mt-2">Create your first repair project above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bridge</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Components</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Planned Year</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {repairProjects.map((project) => (
                  <tr key={project.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{project.project_name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.bridge_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.component_count || 0} components
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.planned_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/repair-planning/${project.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          onClick={() => deleteProject(project.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Project"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Show</span>
                <select
                  value={pagination.itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RepairPlanning;